import { all, call, fork, put, takeLatest, select } from 'redux-saga/effects';
import i18n from 'i18next';

import {
  FETCH_CONSUMPTION_BY_LOCATION, FETCH_AREA_TEMPERATURE_BY_LOCATION, FETCH_CONSUMPTION_BY_FILTERS
} from '../constants/ActionTypes';

import { showError, showMessage } from '../actions/Common';
import { CustomAction } from 'model/state';
import {
  ConsumptionPayload, fetchConsumptionDataSuccess, fetchConsumptionDataError, fetchAreaTemperatureByLocationError,
  fetchAreaTemperatureByLocationSuccess, fetchConsumptionByFiltersSuccess, fetchConsumptionByFiltersError
} from 'actions';
import {
  getSettingsByPeriod, ConsumptionResponse, toConsumptionData, toAreaTemperature, MetricsResponse,
  convertIntervalFromPeriod, ConsumptionParameters, getIntervalFromDateRange, getIntervalValueFromIntervalType
} from 'model/api';
import { getConsumption, getMetrics } from 'api/Consumption';
import { selectUserUnitSystem } from 'selectors';
import { ConsumptionPeriod, MessageType } from 'model';


function* fetchConsumptionByLocationWorker(action: CustomAction<ConsumptionPayload>) {
  const location = action.payload?.location;
  const period = action.payload?.period;
  try {
    if (location === undefined || period === undefined) {
      throw Error('Invalid Parameters');
    }

    const parameters = getSettingsByPeriod(period, location.timezone);
    const response: ConsumptionResponse = yield call(getConsumption, {
      ...parameters,
      locationId: location.id
    });

    const tz = response.params.tz || location.timezone;
    const userUnitSystem = yield select(selectUserUnitSystem());
    yield put(fetchConsumptionDataSuccess(toConsumptionData(response, userUnitSystem, tz), convertIntervalFromPeriod(period)));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchConsumptionDataError());
  }
}

function* fetchAreaTemperatureByLocationWorker(action: CustomAction<ConsumptionPayload>) {
  const macAddress = action.payload?.macAddress;
  const timezone = action.payload?.timezone;
  try {
    if (macAddress === undefined || timezone === undefined) {
      throw Error('Invalid Parameters');
    }

    const parameters = getSettingsByPeriod(ConsumptionPeriod.LAST_HOUR, timezone);
    const response: MetricsResponse = yield call(getMetrics, {
      ...parameters,
      macAddress,
    });

    const userUnitSystem = yield select(selectUserUnitSystem());
    yield put(fetchAreaTemperatureByLocationSuccess(toAreaTemperature(response, userUnitSystem)));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchAreaTemperatureByLocationError());
  }
}

function* fetchConsumptionByFiltersWorker(action: CustomAction<ConsumptionPayload>) {
  const filters = action.payload?.filters;
  const timezone = action.payload?.timezone;
  const userId = action.payload?.userId;
  try {
    if (filters === undefined || timezone === undefined || filters.dateRange?.to === undefined || filters.dateRange.from === undefined) {
      throw Error('Invalid Parameters');
    }

    const intervalType = getIntervalFromDateRange(filters.dateRange.from, filters.dateRange.to);
    const parameters: ConsumptionParameters = {
      startDate: filters.dateRange.from,
      endDate: filters.dateRange.to,
      locationId: filters.locations.length ? filters.locations.map(l => l.id) : undefined,
      userId: filters.locations.length ? undefined : userId,
      interval: getIntervalValueFromIntervalType(intervalType),
      tz: timezone,
    };
    const response: ConsumptionResponse = yield call(getConsumption, parameters);
    const tz = response.params.tz || timezone;

    const userUnitSystem = yield select(selectUserUnitSystem());
    yield put(fetchConsumptionByFiltersSuccess(toConsumptionData(response, userUnitSystem, tz), intervalType));
  } catch (err) {
    if (err.response && err.response.status === 400) {
      yield put(showMessage({ message: i18n.t('consumption-no-data-title'), description: i18n.t('consumption-no-data-desc'), type: MessageType.INFO }));
    } else {
      yield put(showError(i18n.t('connection_error'), err));
    }
    yield put(fetchConsumptionByFiltersError());
  }
}

export function* fetchConsumptionByLocationSaga() {
  yield takeLatest(FETCH_CONSUMPTION_BY_LOCATION, fetchConsumptionByLocationWorker);
}

export function* fetchAreaTemperatureByLocationSaga() {
  yield takeLatest(FETCH_AREA_TEMPERATURE_BY_LOCATION, fetchAreaTemperatureByLocationWorker);
}

export function* fetchConsumptionByFiltersSaga() {
  yield takeLatest(FETCH_CONSUMPTION_BY_FILTERS, fetchConsumptionByFiltersWorker);
}

export default function* consumptionSaga() {
  yield all([
    fork(fetchConsumptionByLocationSaga),
    fork(fetchAreaTemperatureByLocationSaga),
    fork(fetchConsumptionByFiltersSaga),
  ]);
}